import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWindowDimensions, View } from 'react-native';

import colors from 'config/colors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { SettingActions, SettingSelectors } from 'store/Settings';
import { UserSelectors } from 'store/User';
import { getMaxGridColunm } from 'util/getMaxGridColumn';
import { mock } from 'util/mock';
import { HorizontalDivider, InputSpinner } from 'components';
import { BaseText } from 'components/BaseText';

import { Section, SectionContainer } from './components/Section';
import Setting from './components/Setting';

export const CardViewLayout = () => {
  const cardViewLayoutLayout = useAppSelector(
    SettingSelectors.selectCardViewLayoutLayout,
  );
  const cardViewLayoutCollapsibility = useAppSelector(
    SettingSelectors.selectCardViewLayoutCollapsibility,
  );
  const cardViewLayouStatusTab = useAppSelector(
    SettingSelectors.selectCardViewLayoutStatusTab,
  );
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const { maxGridColumn } = getMaxGridColunm();

  const cardViewLayoutAllTabColumn =
    useAppSelector(SettingSelectors.selectCardViewLayoutAllTabColumn) ?? 1;
  const cardViewLayoutNewTabColumn =
    useAppSelector(SettingSelectors.selectCardViewLayoutNewTabColumn) ?? 1;
  const cardViewLayoutCookingTabColumn =
    useAppSelector(SettingSelectors.selectCardViewLayoutCookingTabColumn) ?? 1;

  const featureFlagInterfaceSettings = useAppSelector(
    UserSelectors.selectInterfaceSettings,
  );

  const enableCollapsibleCards =
    featureFlagInterfaceSettings.enable_collapsible_cards;

  return (
    <>
      <Setting
        titleKey="settings.cardViewLayout.title"
        subTitleKey="settings.cardViewLayout.subtitle"
        iconName="view-dashboard"
      >
        <SectionContainer

        // titleKey="settings.cardViewLayout.statusTabs.title"
        >
          <Section
            titleKey={`settings.cardViewLayout.statusTabs.statusTabs.title`}
            subtitleKey={
              cardViewLayouStatusTab === 'split'
                ? 'settings.cardViewLayout.statusTabs.statusTabs.subtitleOptionOn'
                : 'settings.cardViewLayout.statusTabs.statusTabs.subtitleOptionOff'
            }
            switchLeftText={t(
              'settings.cardViewLayout.statusTabs.statusTabs.switchLeftText',
            )}
            switchRightText={t(
              'settings.cardViewLayout.statusTabs.statusTabs.switchRightText',
            )}
            isChecked={cardViewLayouStatusTab === 'split'}
            onPressCheckbox={(value) => {
              dispatch(
                SettingActions.setCardViewLayoutStatusTab(
                  value ? 'split' : 'classic',
                ),
              );
            }}
          />
          <HorizontalDivider style={{ marginVertical: 20 }} />

          <Section
            titleKey={`settings.cardViewLayout.statusTabs.columns.title`}
            childrenStyle={{
              flexGrow: 1,
            }}
          >
            {cardViewLayouStatusTab === 'split' ? (
              <>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <BaseText
                    style={{
                      flex: 1,
                    }}
                  >
                    {t(
                      'settings.cardViewLayout.statusTabs.columns.numberOfColumnsInNewSide.title',
                    )}
                  </BaseText>
                  <InputSpinner
                    key={'new'}
                    value={cardViewLayoutNewTabColumn}
                    onChange={(value) => {
                      dispatch(
                        SettingActions.setCardViewLayoutNewTabColumn(value),
                      );
                    }}
                    min={1}
                    max={maxGridColumn - cardViewLayoutCookingTabColumn}
                    color={
                      cardViewLayoutNewTabColumn >=
                      maxGridColumn - cardViewLayoutCookingTabColumn
                        ? colors.lightRed
                        : colors.main
                    }
                    size={24}
                    iconSize={16}
                    labelSize={15}
                    labelKey={''}
                  />
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <BaseText
                    style={{
                      flex: 1,
                    }}
                  >
                    {t(
                      'settings.cardViewLayout.statusTabs.columns.numberOfColumnsInCookingSide.title',
                    )}
                  </BaseText>
                  <InputSpinner
                    key={'cooking'}
                    value={cardViewLayoutCookingTabColumn}
                    onChange={(value) => {
                      dispatch(
                        SettingActions.setCardViewLayoutCookingTabColumn(value),
                      );
                    }}
                    min={1}
                    max={maxGridColumn - cardViewLayoutNewTabColumn}
                    color={
                      cardViewLayoutCookingTabColumn >=
                      maxGridColumn - cardViewLayoutNewTabColumn
                        ? colors.lightRed
                        : colors.main
                    }
                    size={24}
                    iconSize={16}
                    labelSize={15}
                    labelKey={''}
                  />
                </View>
              </>
            ) : (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <BaseText
                  style={{
                    flex: 1,
                  }}
                >
                  {t(
                    'settings.cardViewLayout.statusTabs.columns.numberOfColumnsInEachTab.title',
                  )}
                </BaseText>
                <InputSpinner
                  key={'all'}
                  value={cardViewLayoutAllTabColumn}
                  onChange={(value) => {
                    dispatch(
                      SettingActions.setCardViewLayoutAllTabColumn(value),
                    );
                  }}
                  min={1}
                  max={maxGridColumn}
                  color={
                    cardViewLayoutAllTabColumn >= maxGridColumn
                      ? colors.lightRed
                      : colors.main
                  }
                  size={24}
                  iconSize={16}
                  labelSize={15}
                  labelKey={''}
                />
              </View>
            )}
          </Section>
        </SectionContainer>
        <SectionContainer>
          <Section
            titleKey={`settings.cardViewLayout.layout.layout.title`}
            subtitleKey={
              cardViewLayoutLayout === 'compact'
                ? 'settings.cardViewLayout.layout.layout.subtitleOptionOn'
                : 'settings.cardViewLayout.layout.layout.subtitleOptionOff'
            }
            isChecked={cardViewLayoutLayout === 'compact'}
            switchLeftText={t(
              'settings.cardViewLayout.layout.layout.switchLeftText',
            )}
            switchRightText={t(
              'settings.cardViewLayout.layout.layout.switchRightText',
            )}
            onPressCheckbox={(value) => {
              dispatch(
                SettingActions.setCardViewLayoutLayout(
                  value ? 'compact' : 'default',
                ),
              );
            }}
          />
        </SectionContainer>
        {enableCollapsibleCards && (
          <SectionContainer>
            <Section
              titleKey={`settings.cardViewLayout.collapsibility.title`}
              subtitleKey={'settings.cardViewLayout.collapsibility.subtitle'}
              isChecked={cardViewLayoutCollapsibility}
              onPressCheckbox={(value) => {
                dispatch(SettingActions.setCardViewLayoutCollapsibility(value));
              }}
            />
          </SectionContainer>
        )}
      </Setting>
    </>
  );
};

//  {/* ------------ START_NOTE: temp ----------------- */}
//       <BaseText>Card View Organisation: {cardViewOrganisation}</BaseText>
//       <View style={{ gap: 4 }}>
//         <BaseButton
//           title="classic"
//           onPress={() => {
//             dispatch(SettingActions.setCardViewOrganisation('classic'));
//           }}
//         />
//         <BaseButton
//           title="split"
//           onPress={() => {
//             dispatch(SettingActions.setCardViewOrganisation('split'));
//           }}
//         />
//       </View>
//       <BaseText>Card View Layout: {cardViewLayout}</BaseText>
//       <View style={{ gap: 4 }}>
//         <BaseButton
//           title="default"
//           onPress={() => {
//             dispatch(SettingActions.setCardViewLayout('default'));
//           }}
//         />
//         <BaseButton
//           title="compact"
//           onPress={() => {
//             dispatch(SettingActions.setCardViewLayout('compact'));
//           }}
//         />
//       </View>
//       {/* ------------ END_NOTE: temp ----------------- */}
